/**
 * Main app UI presentation preferences, persisted to localStorage so they apply at boot
 * before any network call. Theme and density also live on the signed-in user's details
 * (session_scope.ts copies the loaded values in; the setters write changes back), so
 * they follow the user across devices. navRail is device-local. App.vue watches this
 * store and applies the preferences to Vuetify and the document.
 */
import type {IUserDetails} from '@models/user_details';
import {DensityPreference, ThemePreference} from '@shared/enums';
import {gDialogService} from '@src/plugins/dialog_service/dialog_service';
import {useUserStore} from '@src/store/user_store';
import {defineStore} from 'pinia';

export interface IUiState {
   /** Whether the app navigation drawer is collapsed to its icon rail. */
   navRail: boolean;

   /** The user's theme choice; SYSTEM follows the OS preference. */
   themePreference: ThemePreference;

   /** The user's UI density choice. */
   densityPreference: DensityPreference;
}

export const useUiStore = defineStore('ui', {
   state: (): IUiState => ({
      navRail: false,
      themePreference: ThemePreference.SYSTEM,
      densityPreference: DensityPreference.DEFAULT,
   }),

   getters: {
      // Compact is the product default; only COMFORTABLE opts out (design_language/design_language.md)
      isCompactDensity(state): boolean {
         return state.densityPreference !== DensityPreference.COMFORTABLE;
      },

      /** Density prop value for form inputs that opt out of the global defaults swap. */
      inputDensity(): 'comfortable' | 'compact' {
         return this.isCompactDensity ? 'compact' : 'comfortable';
      },

      /** Density prop value for lists and tables that opt out of the global defaults swap. */
      listDensity(): 'default' | 'compact' {
         return this.isCompactDensity ? 'compact' : 'default';
      },
   },

   actions: {
      setNavRail(is_rail: boolean): void {
         this.navRail = is_rail;
      },

      /** Set the theme locally and sync it to the signed-in user's details. */
      setThemePreference(preference: ThemePreference): void {
         this.themePreference = preference;
         void this.savePreferencesToServer({themePreference: preference});
      },

      /** Set the density locally and sync it to the signed-in user's details. */
      setDensityPreference(preference: DensityPreference): void {
         this.densityPreference = preference;
         void this.savePreferencesToServer({densityPreference: preference});
      },

      /** Adopt the preferences stored on the user's details without writing back. */
      applyServerPreferences(details: IUserDetails): void {
         this.themePreference = details.themePreference;
         this.densityPreference = details.densityPreference;
      },

      /** Background write to the user's details; when signed out the change stays local. */
      async savePreferencesToServer(patch: {
         themePreference?: ThemePreference;
         densityPreference?: DensityPreference;
      }): Promise<void> {
         const user_store = useUserStore();
         if (!user_store.isLoaded) {
            return;
         }
         const error = await user_store.save(patch);
         if (error != null) {
            gDialogService.showSnackbar({message: error, type: 'error'});
         }
      },
   },

   persist: true,
});
